import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number as Indian Rupee (INR) currency.
 * Handles null, undefined, or invalid inputs gracefully.
 */
export function formatINR(
  value: number | null | undefined,
  options?: {
    showSign?: boolean;
    precision?: number;
    nullPlaceholder?: string;
  }
): string {
  const {
    showSign = false,
    precision = 2,
    nullPlaceholder = '—',
  } = options || {};

  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return nullPlaceholder;
  }

  const isStrictZero = Object.is(value, 0) || Object.is(value, -0) || Math.abs(value) < 1e-9;
  const sign = showSign && !isStrictZero && value > 0 ? '+' : '';
  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(isStrictZero ? 0 : Math.abs(value));

  if (!isStrictZero && value < 0) {
    return `-₹${formatted}`;
  }

  return `${sign}₹${formatted}`;
}

/**
 * Format a percentage value.
 */
export function formatPercent(
  value: number | null | undefined,
  options?: {
    showSign?: boolean;
    precision?: number;
    nullPlaceholder?: string;
  }
): string {
  const {
    showSign = false,
    precision = 2,
    nullPlaceholder = '—',
  } = options || {};

  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return nullPlaceholder;
  }

  const isStrictZero = Object.is(value, 0) || Object.is(value, -0) || Math.abs(value) < 1e-9;
  const sign = showSign && !isStrictZero && value > 0 ? '+' : '';
  const formattedNumber = (isStrictZero ? 0 : value).toFixed(precision);
  return `${sign}${formattedNumber}%`;
}

/**
 * Format ISO or epoch timestamp into human-readable date/time.
 */
export function formatTimestamp(
  value: string | number | null | undefined,
  format: 'full' | 'time' | 'date' | 'compact' = 'full'
): string {
  if (!value) return '—';

  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);

    const tz = 'Asia/Kolkata';
    switch (format) {
      case 'time':
        return date.toLocaleTimeString('en-IN', {
          timeZone: tz,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });
      case 'date':
        return date.toLocaleDateString('en-IN', {
          timeZone: tz,
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      case 'compact':
        return `${date.toLocaleDateString('en-IN', { timeZone: tz, day: '2-digit', month: 'short' })} ${date.toLocaleTimeString('en-IN', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false })}`;
      case 'full':
      default:
        return `${date.toLocaleDateString('en-IN', { timeZone: tz, day: '2-digit', month: 'short', year: 'numeric' })} ${date.toLocaleTimeString('en-IN', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}`;
    }
  } catch {
    return String(value);
  }
}

/**
 * Format duration in seconds or minutes to human readable string (e.g. 2h 15m or 45s).
 */
export function formatDuration(secondsOrIso: number | string | null | undefined): string {
  if (secondsOrIso === null || secondsOrIso === undefined) return '—';

  let totalSeconds = 0;
  if (typeof secondsOrIso === 'string') {
    // Check if ISO string: calculate diff from now
    const date = new Date(secondsOrIso);
    if (!isNaN(date.getTime())) {
      totalSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    } else {
      totalSeconds = parseInt(secondsOrIso, 10);
    }
  } else {
    totalSeconds = Math.floor(secondsOrIso);
  }

  if (isNaN(totalSeconds) || totalSeconds < 0) return '—';

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Format holding duration from numeric minutes (e.g. from backend holding_minutes) or ISO timestamp.
 * Output: e.g. '7m', '45m', '1h 15m', '2h', '1d', '1d 3h'.
 */
export function formatHoldingDuration(minutesOrIso: number | string | null | undefined): string {
  if (minutesOrIso === null || minutesOrIso === undefined || minutesOrIso === '') return '—';

  let totalMinutes = 0;
  if (typeof minutesOrIso === 'number') {
    totalMinutes = Math.floor(minutesOrIso);
  } else {
    const parsedNum = Number(minutesOrIso);
    if (!isNaN(parsedNum)) {
      totalMinutes = Math.floor(parsedNum);
    } else {
      const date = new Date(minutesOrIso);
      if (!isNaN(date.getTime())) {
        totalMinutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
      } else {
        return '—';
      }
    }
  }

  if (isNaN(totalMinutes) || totalMinutes < 0) return '—';

  const days = Math.floor(totalMinutes / 1440);
  const remMinutes = totalMinutes % 1440;
  const hours = Math.floor(remMinutes / 60);
  const minutes = remMinutes % 60;

  if (days > 0) {
    if (hours > 0) return `${days}d ${hours}h`;
    return `${days}d`;
  }
  if (hours > 0) {
    if (minutes > 0) return `${hours}h ${minutes}m`;
    return `${hours}h`;
  }
  return `${minutes}m`;
}

/**
 * Returns a fallback string if value is null/undefined/empty.
 */
export function formatOrNull(
  value: string | number | null | undefined,
  placeholder: string = 'Pending'
): string {
  if (value === null || value === undefined || value === '') {
    return placeholder;
  }
  return String(value);
}

/**
 * Calculates Stop Loss Buffer %: ((LTP - SL) / LTP) * 100 for LONG positions.
 * Returns signed number or null if inputs are invalid/missing.
 */
export function calculateStopLossBufferPercent(
  ltp: number | null | undefined,
  stopLoss: number | null | undefined
): number | null {
  if (
    ltp === null ||
    ltp === undefined ||
    isNaN(ltp) ||
    !isFinite(ltp) ||
    ltp <= 0 ||
    stopLoss === null ||
    stopLoss === undefined ||
    isNaN(stopLoss) ||
    !isFinite(stopLoss) ||
    stopLoss <= 0
  ) {
    return null;
  }
  const pct = ((ltp - stopLoss) / ltp) * 100;
  return isFinite(pct) ? Math.round(pct * 100) / 100 : null;
}

/**
 * Calculates Target Gap %: ((TP - LTP) / LTP) * 100 for LONG positions.
 * Returns signed number or null if inputs are invalid/missing.
 */
export function calculateTargetGapPercent(
  ltp: number | null | undefined,
  takeProfit: number | null | undefined
): number | null {
  if (
    ltp === null ||
    ltp === undefined ||
    isNaN(ltp) ||
    !isFinite(ltp) ||
    ltp <= 0 ||
    takeProfit === null ||
    takeProfit === undefined ||
    isNaN(takeProfit) ||
    !isFinite(takeProfit) ||
    takeProfit <= 0
  ) {
    return null;
  }
  const pct = ((takeProfit - ltp) / ltp) * 100;
  return isFinite(pct) ? Math.round(pct * 100) / 100 : null;
}

/**
 * Clamps a numeric percentage value between min and max (default 0 and 100).
 */
export function clampPercentage(value: number, min = 0, max = 100): number {
  if (isNaN(value) || !isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * Calculates the relative position percentage of a price within [minPrice, maxPrice].
 * Safely clamps result to [0, 100] and returns 50 if range is 0 or invalid.
 */
export function calculateRangePosition(
  price: number,
  minPrice: number,
  maxPrice: number
): number {
  if (
    isNaN(price) ||
    !isFinite(price) ||
    isNaN(minPrice) ||
    !isFinite(minPrice) ||
    isNaN(maxPrice) ||
    !isFinite(maxPrice) ||
    maxPrice <= minPrice
  ) {
    return 50;
  }
  const pct = ((price - minPrice) / (maxPrice - minPrice)) * 100;
  return clampPercentage(pct, 0, 100);
}
